<script setup lang="ts">
/** Renders confirmDialog() questions as a modal <dialog> (focus trap, Esc = cancel, top layer over drawers). */
import { nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { pendingConfirm } from '../stores/confirm';

const { t } = useI18n();
const dlg = ref<HTMLDialogElement | null>(null);
const okBtn = ref<HTMLButtonElement | null>(null);
const field = ref<HTMLInputElement | null>(null);
const text = ref('');

watch(pendingConfirm, async (req) => {
  await nextTick();
  if (req && dlg.value && !dlg.value.open) {
    text.value = req.input ?? '';
    dlg.value.showModal();
    await nextTick();
    if (req.input !== undefined) field.value?.select();
    else okBtn.value?.focus();
  } else if (!req && dlg.value?.open) dlg.value.close();
});
const answer = (ok: boolean) => pendingConfirm.value?.resolve(ok, text.value);
</script>

<template>
  <dialog
    ref="dlg"
    class="m-auto w-[min(28rem,calc(100vw-2rem))] rounded-xl border border-slate-200 bg-white p-0 text-slate-900 shadow-2xl backdrop:bg-slate-950/50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
    aria-labelledby="confirm-msg"
    @cancel.prevent="answer(false)"
    @click.self="answer(false)"
  >
    <div v-if="pendingConfirm" class="p-5">
      <p id="confirm-msg" class="text-sm leading-relaxed whitespace-pre-line">{{ pendingConfirm.message }}</p>
      <input
        v-if="pendingConfirm.input !== undefined"
        ref="field"
        v-model="text"
        class="input mt-3"
        aria-labelledby="confirm-msg"
        @keydown.enter.prevent="answer(true)"
      />
      <div class="mt-5 flex justify-end gap-2">
        <button type="button" class="btn-secondary" @click="answer(false)">{{ t('common.cancel') }}</button>
        <button
          ref="okBtn"
          type="button"
          :class="pendingConfirm.danger ? 'btn-primary !bg-red-600 hover:!bg-red-700' : 'btn-primary'"
          @click="answer(true)"
        >
          {{ pendingConfirm.okLabel ?? t('ui.ok') }}
        </button>
      </div>
    </div>
  </dialog>
</template>
